import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { Building2, TrendingUp, TrendingDown, AlertTriangle, ArrowRight } from 'lucide-react'
import { useAuth } from '../../context/AuthContext'
import { useIsMultiFacility } from '../../hooks/useTier'
import { getFacilities, type FacilityOption } from '../../lib/services/facilities'
import { getComplianceTrends } from '../../lib/services/dashboards'
import { getCorrectiveActionSummary } from '../../lib/services/dashboards'
import { getCorrectiveActions } from '../../lib/services/correctiveActions'
import { cmsStatusTone } from '../../lib/services/scoringPresentation'
import { StandaloneDashboard } from './StandaloneDashboard'
import { ConsultantDashboard } from './ConsultantDashboard'

/**
 * Enterprise dashboard routes to the correct sub-view:
 * - Corporate (superadmin / org-wide admin): portfolio scorecard
 * - Regional (admin scoped to facility subset): consultant-like view
 * - Facility (single facility): standalone-like view + corporate directives
 */
export function EnterpriseDashboard() {
  const { viewerContext } = useAuth()
  const isMultiFacility = useIsMultiFacility()
  const isCorporate = viewerContext?.is_superadmin === true

  // Corporate superadmin gets the portfolio view
  if (isCorporate) return <CorporateDashboard />

  // Regional (multi-facility non-superadmin) gets consultant-like view
  if (isMultiFacility) return <ConsultantDashboard />

  // Facility-level gets standalone + corporate directives
  return <FacilityDashboard />
}

function CorporateDashboard() {
  const facilitiesQuery = useQuery({
    queryKey: ['dashboard-facilities'],
    queryFn: getFacilities,
    staleTime: 5 * 60 * 1000,
  })

  const trendsQuery = useQuery({
    queryKey: ['dashboard-trends'],
    queryFn: () => getComplianceTrends(365),
    staleTime: 5 * 60 * 1000,
  })

  const capaQuery = useQuery({
    queryKey: ['dashboard-capa'],
    queryFn: () => getCorrectiveActionSummary(),
    staleTime: 2 * 60 * 1000,
  })

  const actionsQuery = useQuery({
    queryKey: ['dashboard-actions'],
    queryFn: () => getCorrectiveActions(),
    staleTime: 2 * 60 * 1000,
  })

  const facilities = facilitiesQuery.data ?? []
  const latest = trendsQuery.data?.[trendsQuery.data.length - 1]
  const previous = trendsQuery.data?.[trendsQuery.data.length - 2]
  const scoreDelta = latest && previous ? latest.overall_score - previous.overall_score : 0
  const latestTone = latest ? cmsStatusTone(latest.cms_outcome) : { text: 'text-slate-600', bg: 'bg-slate-100' }
  const capa = capaQuery.data ?? { open: 0, in_progress: 0, completed: 0, verified: 0, overdue: 0 }
  const totalActions = capa.open + capa.in_progress + capa.completed + capa.verified
  const completionPct = totalActions > 0 ? Math.round(((capa.completed + capa.verified) / totalActions) * 100) : 0

  const actions = actionsQuery.data ?? []
  const overdueByFacility = new Map<string, number>()
  for (const a of actions) {
    if (a.status === 'overdue' && a.visit?.facility_id) {
      overdueByFacility.set(a.visit.facility_id, (overdueByFacility.get(a.visit.facility_id) ?? 0) + 1)
    }
  }

  function facilityStatus(f: FacilityOption): 'green' | 'amber' | 'red' {
    const overdue = overdueByFacility.get(f.facility_id) ?? 0
    if (overdue >= 3) return 'red'
    if (overdue >= 1) return 'amber'
    return 'green'
  }

  const statusColors = { green: 'bg-emerald-500', amber: 'bg-amber-500', red: 'bg-rose-500' }

  return (
    <div className="mx-auto max-w-6xl space-y-5">
      {/* Greeting */}
      <section className="v11-workspace-header">
        <p className="text-xs font-bold uppercase tracking-wider text-blue-600">Enterprise Portfolio</p>
        <h1 className="mt-1 font-brand text-2xl font-bold text-[var(--brand-deep)]">
          Portfolio Overview
        </h1>
        <p className="mt-1 text-sm text-slate-500">
          {facilities.length} facilit{facilities.length !== 1 ? 'ies' : 'y'} &middot; {capa.overdue} overdue action{capa.overdue !== 1 ? 's' : ''} across portfolio
        </p>
      </section>

      <Link
        to="/visits"
        className="v11-dashboard-card block p-5"
      >
        <p className="text-xs font-black uppercase tracking-widest text-[var(--brand-secondary)]">
          Facility operations
        </p>
        <h2 className="mt-1 font-brand text-lg font-extrabold text-[var(--brand-deep)]">
          Drill into a facility before opening facility-level modules
        </h2>
        <p className="mt-1 text-sm text-slate-500">
          Portfolio navigation stays executive-level. Chart Audit, Insurance, Credentialing, PAT Intake, and Patient Communications become available from the selected facility context.
        </p>
      </Link>

      {/* Portfolio Scorecard */}
      <section className="v11-kpi-grid-four">
        <div className="v11-kpi-card">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Facilities</p>
          <span className="mt-2 block font-brand text-3xl font-extrabold text-[var(--brand-deep)]">{facilities.length}</span>
          <p className="mt-1 text-xs text-slate-500">Active locations</p>
        </div>
        <div className="v11-kpi-card v11-kpi-accent-teal">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Compliance</p>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="font-brand text-3xl font-extrabold text-[var(--brand-deep)]">{latest?.overall_score ?? '—'}</span>
            {scoreDelta !== 0 && (
              <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-bold ${scoreDelta > 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
                {scoreDelta > 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                {scoreDelta > 0 ? '+' : ''}{scoreDelta}
              </span>
            )}
          </div>
          <p className={`mt-1 text-xs font-semibold ${latestTone.text}`}>{latest?.cms_outcome_label ?? 'No data'}</p>
        </div>
        <div className="v11-kpi-card v11-kpi-accent-gold">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-500">CAPA Progress</p>
          <span className="mt-2 block font-brand text-3xl font-extrabold text-[var(--brand-deep)]">{completionPct}%</span>
          <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-slate-100">
            <div className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-teal-400" style={{ width: `${completionPct}%` }} />
          </div>
        </div>
        <div className="v11-kpi-card v11-kpi-accent-rose">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Overdue</p>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="font-brand text-3xl font-extrabold text-[var(--brand-deep)]">{capa.overdue}</span>
            {capa.overdue > 0 && <AlertTriangle size={16} className="text-rose-500" />}
          </div>
          <p className="mt-1 text-xs text-slate-500">{capa.overdue > 0 ? 'Across portfolio' : 'All clear'}</p>
        </div>
      </section>

      {/* Facility Status Grid */}
      <section className="v11-section">
        <div className="v11-section-header">
          <h2 className="font-brand text-sm font-bold text-[var(--brand-deep)]">Facility Status</h2>
          <Link to="/visits" className="text-xs font-bold text-[var(--brand-primary)] hover:underline">
            View all <ArrowRight size={10} className="inline" />
          </Link>
        </div>
        <div className="grid grid-cols-1 gap-px bg-slate-100 sm:grid-cols-2 lg:grid-cols-3">
          {facilities.map(f => {
            const status = facilityStatus(f)
            const overdue = overdueByFacility.get(f.facility_id) ?? 0
            return (
              <Link key={f.facility_id} to={`/visits?facility=${f.facility_id}`} className="flex items-center gap-3 bg-white px-5 py-4 transition-all hover:bg-slate-50 hover:shadow-sm">
                <span className={`h-3 w-3 rounded-full ${statusColors[status]}`} />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-bold text-slate-800">{f.facility_name}</p>
                  <p className="text-xs text-slate-400">
                    {f.city ?? ''}{f.state ? `, ${f.state}` : ''}
                    {overdue > 0 && <span className="ml-2 font-bold text-rose-500">{overdue} overdue</span>}
                  </p>
                </div>
                <ArrowRight size={14} className="text-slate-300" />
              </Link>
            )
          })}
        </div>
      </section>
    </div>
  )
}

function FacilityDashboard() {
  const actionsQuery = useQuery({
    queryKey: ['dashboard-corporate-actions'],
    queryFn: () => getCorrectiveActions(),
    staleTime: 2 * 60 * 1000,
  })

  const corporateActions = (actionsQuery.data ?? []).filter(a =>
    // Actions with parent_action_id are corporate-pushed
    'parent_action_id' in a && (a as Record<string, unknown>).parent_action_id
  )

  return (
    <div className="space-y-5">
      <StandaloneDashboard />

      {/* Corporate Directives section */}
      {corporateActions.length > 0 && (
        <section className="mx-auto max-w-5xl">
          <div className="v11-section">
            <div className="v11-section-header justify-start gap-2 bg-blue-50">
              <Building2 size={14} className="text-blue-600" />
              <h2 className="font-brand text-sm font-bold text-blue-800">Corporate Directives</h2>
              <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-bold text-blue-600">{corporateActions.length}</span>
            </div>
            <div className="p-2">
              {corporateActions.map(a => (
                <Link key={a.action_id} to={`/actions/${a.action_id}`} className="v11-list-link">
                  <div>
                    <p className="text-sm font-bold text-slate-800">{a.title}</p>
                    <p className="text-xs text-slate-400">Due: {a.due_date ?? 'Not set'}</p>
                  </div>
                  <span className={a.status === 'overdue' ? 'badge-archived' : 'badge-in-progress'}>{a.status}</span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  )
}
